import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import TimelineIcon from '@mui/icons-material/Timeline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import { buildEventsApiUrl } from '../config/api';

interface MathTradeRunPanelProps {
  eventId: string;
  canRun: boolean;
  userId?: string;
}

interface TradeSummaryRow {
  vertex: string;
  receives: string;
  sends: string;
  vertexLabel?: string;
  receivesLabel?: string;
  sendsLabel?: string;
}

interface MathTradeRunResult {
  id?: string;
  createdAt?: string;
  result?: {
    warnings?: string[];
    errors?: string[];
    numTrades?: number;
    numGroups?: number;
    ratio?: number;
    items?: number;
    summary?: {
      traded?: TradeSummaryRow[];
      notTrades?: Array<{ vertex: string; vertexLabel?: string }>;
    };
  };
  requestPayload?: any;
  diagnostics?: {
    excludedPreferences?: Array<{ offeredItemID: string; targetItemID?: string | null; reason: string }>;
  };
}

interface MathTradeStatusPayload {
  inputState: 'collecting' | 'locked';
  previewRunId: string | null;
  publishedRunId: string | null;
  publishedIsFinal: boolean;
  lastPreviewAt: string | null;
  publishedAt: string | null;
  lockedAt: string | null;
  cutoffAt: string | null;
  isOwner: boolean;
  tradeCutoffPassed: boolean;
  itemCount: number;
  visibleRunId: string | null;
  visibleRun: MathTradeRunResult | null;
  previewVisible: boolean;
  publishedVisible: boolean;
  participantSummary?: {
    matched: Array<{
      giveItem: string;
      giveTo: string;
      receiveItem: string;
      receiveFrom: string;
    }>;
    unmatched: Array<{ item: string }>;
  };
}

const splitTradeLabel = (label?: string) => {
  if (!label) return { trader: '', item: '' };
  const match = label.match(/^(\([^)]*\))\s+(.+)$/);
  if (!match) return { trader: '', item: label };
  return { trader: match[1], item: match[2] };
};

const buildTradeGroups = (rows: TradeSummaryRow[]) => {
  const remaining = new Map(rows.map((row) => [row.vertex, row]));
  const groups: TradeSummaryRow[][] = [];

  while (remaining.size > 0) {
    const start = remaining.values().next().value as TradeSummaryRow;
    const group: TradeSummaryRow[] = [];
    let current: TradeSummaryRow | undefined = start;
    const seen = new Set<string>();

    while (current && !seen.has(current.vertex)) {
      seen.add(current.vertex);
      group.push(current);
      remaining.delete(current.vertex);
      current = remaining.get(current.sends);
    }

    groups.push(group);
  }

  return groups.sort((a, b) => b.length - a.length);
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
};

const MathTradeRunPanel: React.FC<MathTradeRunPanelProps> = ({ eventId, canRun, userId }) => {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<MathTradeStatusPayload | null>(null);
  const [run, setRun] = useState<MathTradeRunResult | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/math-trade/status${query}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load math trade status');
      }

      const nextStatus = data.mathTrade as MathTradeStatusPayload;
      setStatus(nextStatus);
      setRun(nextStatus.visibleRun || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load math trade status');
    } finally {
      setLoading(false);
    }
  }, [eventId, userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRunPreview = async () => {
    try {
      setRunning(true);
      setError(null);

      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/run-math-trade`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, mode: 'preview' })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to run preview');
      }

      setRun({
        id: data.runId,
        createdAt: new Date().toISOString(),
        result: data.result,
        requestPayload: data.payload,
        diagnostics: data.diagnostics
      });
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to run preview');
    } finally {
      setRunning(false);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/math-trade/publish`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to publish preview');
      }
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to publish preview');
    } finally {
      setPublishing(false);
    }
  };

  const tradeCount = run?.result?.numTrades || 0;
  const groupCount = run?.result?.numGroups || 0;
  const totalItems = run?.result?.items || run?.requestPayload?.data?.TradeWants?.length || status?.itemCount || 0;
  const matchedRatio = typeof run?.result?.ratio === 'number' ? `${Math.round((run.result.ratio || 0) * 100)}%` : '0%';
  const tradeRows = run?.result?.summary?.traded || [];
  const tradeGroups = buildTradeGroups(tradeRows);
  const isOwner = Boolean(status?.isOwner && canRun);
  const tradeLocked = Boolean(status?.tradeCutoffPassed || status?.inputState === 'locked');
  const previewModeActive = Boolean(isOwner && status?.previewRunId && status?.visibleRunId === status.previewRunId && !status?.publishedIsFinal);
  const publishedProvisional = Boolean(status?.publishedRunId && !status?.publishedIsFinal);
  const publishedFinal = Boolean(status?.publishedIsFinal && (status?.publishedRunId || status?.publishedAt));
  const finalWithoutRun = Boolean(publishedFinal && !status?.publishedRunId);
  const waitingForFinalization = Boolean(tradeLocked && !publishedFinal);
  const stageLabel = publishedFinal
    ? 'Final Results Published'
    : previewModeActive
      ? 'Owner Preview Active'
      : publishedProvisional
        ? 'Published Preview Visible'
        : waitingForFinalization
          ? 'Locked Awaiting Finalization'
          : tradeLocked
            ? 'Locked'
            : status?.previewRunId
              ? 'Preview Ready'
              : 'Collecting Wants';
  const stageColor: 'success' | 'warning' | 'info' | 'default' = publishedFinal
    ? 'success'
    : (previewModeActive || waitingForFinalization)
      ? 'warning'
      : (publishedProvisional || status?.previewRunId)
        ? 'info'
        : 'default';
  const cutoffLabel = formatTimestamp(status?.cutoffAt);
  const lockedLabel = formatTimestamp(status?.lockedAt);
  const publishedLabel = formatTimestamp(status?.publishedAt);
  const previewLabel = formatTimestamp(status?.lastPreviewAt);
  const runCreatedLabel = formatTimestamp(run?.createdAt);
  const panelDescription = isOwner
    ? publishedFinal
      ? finalWithoutRun
        ? 'Final results are marked published for this event, but no run details were stored.'
        : 'Final results are locked in and visible for this event.'
      : waitingForFinalization
        ? 'Inputs are locked. Finalization will publish the final result for attendees.'
        : publishedProvisional
          ? 'A published preview is visible to participants. You can still rerun preview until cutoff.'
          : previewModeActive
            ? 'You are viewing the current owner-only preview.'
            : 'Collect wants, run private previews, then publish when you are ready.'
    : publishedFinal
      ? finalWithoutRun
        ? 'Final results were published for this event, but no run details are available to display.'
        : 'These final math trade results are locked in for the event.'
      : publishedProvisional
        ? 'These published preview results may still change before cutoff.'
        : tradeLocked
          ? 'Trade inputs are locked for this event.'
          : 'Published math trade results will appear here when available.';

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', mb: 3, backgroundColor: '#f8f9fa' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            Math Trade Runs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {panelDescription}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={stageLabel} color={stageColor} />
          {isOwner && (
            <Button
              variant="contained"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRunPreview}
              disabled={running || tradeLocked}
            >
              {running ? 'Running…' : status?.previewRunId ? 'Rerun Preview' : 'Run Preview'}
            </Button>
          )}
          {isOwner && status?.previewRunId && (
            <Button
              variant="outlined"
              color="success"
              startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
              onClick={handlePublish}
              disabled={publishing || tradeLocked}
            >
              {publishing ? 'Publishing…' : 'Publish Preview'}
            </Button>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {status?.tradeCutoffPassed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Trade cutoff has passed. Items and want lists are locked for this event.
        </Alert>
      )}

      {waitingForFinalization && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Inputs are locked. Finalization should publish the locked-in results shortly.
        </Alert>
      )}

      {publishedFinal && !run && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {finalWithoutRun
            ? 'Final trade results were published for this event, but no run details were saved.'
            : 'Final trade results have already been published for this event.'}
        </Alert>
      )}

      {previewModeActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Preview only — visible to the event owner. This is not final and may change before publication or cutoff.
        </Alert>
      )}

      {publishedProvisional && !previewModeActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Published preview — participants can see where things stand, but this is not final until the cutoff is reached.
        </Alert>
      )}

      {publishedFinal && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {finalWithoutRun
            ? 'Final published result — this event is locked, but no run details were saved.'
            : 'Final published result — this run is locked in for the upcoming trade event.'}
        </Alert>
      )}

      {!loading && status && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {cutoffLabel && <Chip label={`Cutoff: ${cutoffLabel}`} variant="outlined" />}
          {previewLabel && <Chip label={`Preview updated: ${previewLabel}`} variant="outlined" />}
          {publishedLabel && <Chip label={`Published: ${publishedLabel}`} variant="outlined" color="success" />}
          {lockedLabel && <Chip label={`Locked: ${lockedLabel}`} variant="outlined" color="warning" />}
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : run ? (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
            <Chip icon={<TimelineIcon />} label={`Trades: ${tradeCount}`} color={tradeCount > 0 ? 'success' : 'default'} />
            <Chip icon={<RuleFolderIcon />} label={`Groups: ${groupCount}`} color="primary" variant="outlined" />
            <Chip icon={<Inventory2Icon />} label={`Items in run: ${totalItems}`} color="secondary" variant="outlined" />
            <Chip label={`Matched ratio: ${matchedRatio}`} variant="outlined" />
            {runCreatedLabel && <Chip label={`Run captured: ${runCreatedLabel}`} variant="outlined" />}
          </Stack>

          {Array.isArray(run.result?.errors) && run.result.errors.length > 0 && (
            <Alert severity="warning">{run.result.errors.join(' | ')}</Alert>
          )}

          {Array.isArray(run.result?.warnings) && run.result.warnings.length > 0 && (
            <Alert severity="info">{run.result.warnings.join(' | ')}</Alert>
          )}

          {!isOwner && status?.participantSummary && (status.participantSummary.matched.length > 0 || status.participantSummary.unmatched.length > 0) && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Your Trade Summary</Typography>
              <Stack spacing={1}>
                {status.participantSummary.matched.map((trade, index) => (
                  <Paper key={`participant-trade-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Bring:</strong> {trade.giveItem}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Give to:</strong> {trade.giveTo || 'Unknown trader'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>You receive:</strong> {trade.receiveItem}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Receive from:</strong> {trade.receiveFrom || 'Unknown trader'}
                    </Typography>
                  </Paper>
                ))}
                {status.participantSummary.unmatched.length > 0 && (
                  <Alert severity="info">
                    No trade was assigned for: {status.participantSummary.unmatched.map((row) => row.item).join(', ')}
                  </Alert>
                )}
              </Stack>
            </Box>
          )}

          {tradeRows.length > 0 ? (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Resolved Trades</Typography>
              <Stack spacing={2}>
                {tradeGroups.map((group, groupIndex) => (
                  <Paper key={`group-${groupIndex}`} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Trade Group {groupIndex + 1} — {group.length}-way trade
                    </Typography>
                    <Stack spacing={1}>
                      {group.map((trade, index) => {
                        const giving = splitTradeLabel(trade.vertexLabel || trade.vertex);
                        const receiving = splitTradeLabel(trade.receivesLabel || trade.receives);
                        const counterparty = splitTradeLabel(trade.sendsLabel || trade.sends);

                        return (
                          <Paper key={`${trade.vertex}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                              {giving.trader || 'Trader'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Give:</strong> {giving.item} to {counterparty.trader || 'Unknown trader'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Receive:</strong> {receiving.item} from {receiving.trader || 'Unknown trader'}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : (
            <Alert severity="info">No trades were found in the visible run.</Alert>
          )}

        </Stack>
      ) : (
        <Alert severity={publishedFinal ? 'success' : 'info'}>
          {publishedFinal
            ? 'Final trade results were published for this event, but no run details are attached to this event record.'
            : waitingForFinalization
              ? 'Inputs are locked and waiting for finalization. Final results should appear here once processing completes.'
              : isOwner
                ? 'No preview or published math trade run is available yet. Collect wants, then run a preview when you are ready.'
                : 'No published math trade results are available yet.'}
        </Alert>
      )}
    </Paper>
  );
};

export default MathTradeRunPanel;
